import { Routes, Route, Navigate } from 'react-router-dom';
import { useColors } from '@mastimanch/ui-components';
import { Toaster } from 'react-hot-toast';
import { ProtectedRoute, useAuthContext, UserRole } from './auth-config';
import MobileRestrictionGuard from './components/MobileRestrictionGuard';
import { FocusTrackingProvider } from './context/FocusTrackingContext';
import StudentNavbar from '@components/Layout/StudentNavbar';
import DashboardPage from '@pages/DashboardPage';
import CourseCatalogPage from '@pages/CourseCatalogPage';
import VideoPlayerPage from '@pages/VideoPlayerPage';
import AssignmentsPage from '@pages/AssignmentsPage';
import ProgressPage from '@pages/ProgressPage';
import ProfilePage from '@pages/ProfilePage';
import LoginPage from '@pages/LoginPage';
import RegisterPage from '@pages/RegisterPage';
import RegisterPageNew from '@pages/RegisterPageNew';
import ForgotPasswordPage from '@pages/ForgotPasswordPage';

function App() {
  const colors = useColors();
  const { isAuthenticated, isLoading, user, signOut } = useAuthContext();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: colors.backgroundLight }}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" style={{ borderColor: colors.primaryBlue }}></div>
      </div>
    );
  }

  if (isAuthenticated && user && user.role !== UserRole.STUDENT) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md p-8 bg-white rounded-2xl shadow-xl border border-gray-100 flex flex-col items-center justify-center gap-6">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center text-red-500">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0-10.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.249-8.25-3.286Zm0 13.036h.008v.008H12v-.008Z" />
            </svg>
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-gray-900">Unauthorized User Role</h2>
            <p className="text-gray-600 text-sm">Only Students can sign in.</p>
          </div>
          <button
            onClick={() => signOut()}
            className="w-full px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition duration-200 shadow-md shadow-red-100 flex items-center justify-center gap-2"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <MobileRestrictionGuard>
      <FocusTrackingProvider>
        <div className="min-h-screen bg-gray-50 transition-colors duration-300" style={{ color: colors.textPrimary, backgroundColor: colors.backgroundLight }}>
          {/* Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: colors.backgroundCard,
                color: colors.textPrimary,
                border: `1px solid ${colors.borderLight}`,
              },
            }}
          />

          <Routes>
            {/* Root redirect based on auth status */}
            <Route path="/" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />} />

            {/* Public Routes (redirect to dashboard if already logged in) */}
            <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
            <Route path="/register" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPageNew />} />
            <Route path="/register-old" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />} />
            <Route path="/forgot-password" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />} />

            {/* Protected Routes with Sidebar and Navbar layout */}
            <Route
              path="/*"
              element={
                <div className="flex flex-col min-h-screen">
                  {/* Navbar */}
                  <StudentNavbar />

                  {/* Main Content Area */}
                  <main className="flex-1">
                    <Routes>
                      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                      <Route path="/courses" element={<ProtectedRoute><CourseCatalogPage /></ProtectedRoute>} />
                      <Route path="/courses/:bookId" element={<ProtectedRoute><CourseCatalogPage /></ProtectedRoute>} />
                      <Route path="/courses/:bookId/:videoId" element={<ProtectedRoute><CourseCatalogPage /></ProtectedRoute>} />
                      <Route path="/video/:courseId/:chapterId" element={<ProtectedRoute><VideoPlayerPage /></ProtectedRoute>} />
                      <Route path="/watch/lecture/:videoId/:bookId" element={<ProtectedRoute><VideoPlayerPage /></ProtectedRoute>} />
                      <Route path="/watch/lecture/:videoId" element={<ProtectedRoute><VideoPlayerPage /></ProtectedRoute>} />
                      <Route path="/assignments" element={<ProtectedRoute><AssignmentsPage /></ProtectedRoute>} />
                      <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
                      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

                      {/* 404 - Redirect to dashboard if logged in, otherwise login */}
                      <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />} />
                    </Routes>
                  </main>
                </div>
              }
            />
          </Routes>
        </div>
      </FocusTrackingProvider>
    </MobileRestrictionGuard>
  );
}

export default App;